import {
  getDefaultAscPath,
  getCurrentValidYtdPeriodName,
  isYtdPeriodName,
  PeriodName,
  RedirectState,
  useCurrentCompanyId,
  useFiscalYear,
  useSelectedFiscalYearIdOptional,
  useSelectedPeriodNameOptional,
} from '@neo.tax/shared';

/**
 * Redirects the ASC home route to the current valid period when the URL period is stale for today's date.
 *
 * Two cases are corrected:
 * - a stale YTD (e.g. history holds YTD5 but we are now in month 6), and
 * - Annual (`A`) on a fiscal year that has not finished yet. `A` spans the entire fiscal year, so an
 *   `A` route carried onto an in-progress year (bookmark, old tab, or history from a completed year)
 *   lets allocation saves write future-month rows and push project end dates into the future (ASC-130).
 *   For an in-progress year `getCurrentValidYtdPeriodName` returns the current YTD (not `A`), so we
 *   redirect; once the year is complete it returns `A` and Annual is left untouched.
 *
 * Specific quarter/month periods are intentionally not validated here — they are chosen explicitly and
 * the picker already excludes future months.
 */
export const useAscHomeRedirect = (): RedirectState => {
  const periodName = useSelectedPeriodNameOptional();
  const fiscalYearId = useSelectedFiscalYearIdOptional();
  const companyId = useCurrentCompanyId();
  const { fiscalYear, loading } = useFiscalYear({ fiscalYearId });

  if (loading) {
    return { isLoading: true };
  }

  // Only validate YTD and Annual periods (both can be stale relative to today's date).
  if (!periodName || !fiscalYear || !(isYtdPeriodName(periodName) || periodName === PeriodName.A)) {
    return { isLoading: false };
  }

  // Redirect if the selected period isn't the current valid period for today's date.
  const currentValidPeriod = getCurrentValidYtdPeriodName(fiscalYear);
  if (currentValidPeriod && fiscalYearId && periodName !== currentValidPeriod) {
    return {
      isLoading: false,
      redirectTo: getDefaultAscPath({ companyId, fiscalYearId, periodName: currentValidPeriod }),
    };
  }

  return { isLoading: false };
};
